import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  FormActionButtons,
  FormField,
  TextInput,
  ToggleInput,
} from "@a2z/ui";
import { useTranslations } from "@/lib/i18n/intl";
import { useLocale } from "@seller/app/locale";
import { keys } from "@/lib/i18n/portal";
import { formatCurrency } from "@/lib/formatting/currency";
import type { CouponView } from "@/lib/api/types";
import { useCreateCouponMutation } from "@/modules/coupons/services/useCreateCouponMutation";
import { useUpdateCouponMutation } from "@/modules/coupons/services/useUpdateCouponMutation";
import { PromotionFields } from "@/modules/coupons/components/PromotionFields";
import {
  buildPromotionPayloadFields,
  couponPromotionKeys,
  initialPromotionState,
  validatePromotionState,
  type PromotionFieldErrors,
  type PromotionState,
} from "@/modules/coupons/lib/promotion-form";

type Mode = "create" | "edit";

type CouponFormProps = {
  storeSlug: string;
  mode: Mode;
  coupon?: CouponView;
  onCancel: () => void;
  onSuccess: () => void;
  /**
   * When provided, the form renders without inline Cancel/Save buttons and
   * exposes its `id` so an external Save button (e.g. inside a Modal footer)
   * can submit it via the native `form="<id>"` attribute.
   */
  formId?: string;
  /** Called whenever the submit-in-flight state flips. Used by the parent to
   * disable the external footer button. */
  onSubmittingChange?: (busy: boolean) => void;
};

type FieldErrors = {
  code?: string;
  maxUses?: string;
  minOrderAmount?: string;
};

const CODE_PATTERN = /^[A-Z0-9_-]+$/;
const CODE_MIN_LENGTH = 3;
const CODE_MAX_LENGTH = 32;

/** ISO string → `datetime-local` input value (YYYY-MM-DDTHH:mm) in local time. */
function isoToDatetimeLocal(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
}

export function CouponForm({
  storeSlug,
  mode,
  coupon,
  onCancel,
  onSuccess,
  formId,
  onSubmittingChange,
}: CouponFormProps) {
  const t = useTranslations();
  const locale = useLocale();
  const [code, setCode] = useState(coupon?.code ?? "");
  const [codeTouched, setCodeTouched] = useState(false);
  const [promo, setPromo] = useState<PromotionState>(() =>
    initialPromotionState(coupon),
  );
  const [promoErrors, setPromoErrors] = useState<PromotionFieldErrors>({});
  const [maxUses, setMaxUses] = useState<string>(
    coupon?.maxUses !== null && coupon?.maxUses !== undefined
      ? String(coupon.maxUses)
      : "",
  );
  const [minOrderAmount, setMinOrderAmount] = useState<string>(
    coupon?.minOrderAmount !== null && coupon?.minOrderAmount !== undefined
      ? String(coupon.minOrderAmount)
      : "",
  );
  const [expiresAt, setExpiresAt] = useState<string>(
    coupon?.expiresAt ? isoToDatetimeLocal(coupon.expiresAt) : "",
  );
  const [active, setActive] = useState(coupon?.active ?? true);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { mutateAsync: createCoupon, isPending: isCreating } =
    useCreateCouponMutation();
  const { mutateAsync: updateCoupon, isPending: isUpdating } =
    useUpdateCouponMutation();
  const isSubmitting = isCreating || isUpdating;

  const normalizedCode = code.trim().toUpperCase();
  const isCodeValidFormat =
    normalizedCode.length >= CODE_MIN_LENGTH &&
    normalizedCode.length <= CODE_MAX_LENGTH &&
    CODE_PATTERN.test(normalizedCode);
  const codeIsValid = code.length > 0 && isCodeValidFormat;

  const validateCode = (): string | undefined => {
    if (
      normalizedCode.length < CODE_MIN_LENGTH ||
      normalizedCode.length > CODE_MAX_LENGTH
    ) {
      return t(keys.coupons.errorCodeLength);
    }
    if (!CODE_PATTERN.test(normalizedCode)) {
      return t(keys.coupons.errorCodePattern);
    }
    return undefined;
  };

  const updatePromo = (patch: Partial<PromotionState>) =>
    setPromo((prev) => ({ ...prev, ...patch }));
  const clearPromoError = (field: keyof PromotionFieldErrors) =>
    setPromoErrors((prev) => ({ ...prev, [field]: undefined }));

  const validate = (): FieldErrors | null => {
    const errors: FieldErrors = {};
    const codeError = validateCode();
    if (codeError) errors.code = codeError;

    if (maxUses.trim().length > 0) {
      const numericMaxUses = Number(maxUses);
      if (
        Number.isNaN(numericMaxUses) ||
        !Number.isInteger(numericMaxUses) ||
        numericMaxUses < 1
      ) {
        errors.maxUses = t(keys.coupons.errorMaxUsesInvalid);
      }
    }

    if (minOrderAmount.trim().length > 0) {
      const numericMinOrder = Number(minOrderAmount);
      if (Number.isNaN(numericMinOrder) || numericMinOrder <= 0) {
        errors.minOrderAmount = t(keys.coupons.errorMinOrderInvalid);
      }
    }

    return Object.keys(errors).length > 0 ? errors : null;
  };

  const previewText = useMemo(() => {
    const codeLabel = normalizedCode || t(keys.coupons.previewPlaceholderCode);
    const { kind, discountUnit, discountValue } = promo;
    if (kind === "FREE_DELIVERY") {
      return t(keys.coupons.previewFreeDelivery, { code: codeLabel });
    }
    if (kind === "BUY_X_GET_Y") {
      const buy = Number(promo.buyQuantity);
      const get = Number(promo.getQuantity);
      const percent = Number(promo.getDiscountPercent);
      const ready =
        promo.buyQuantity.trim().length > 0 &&
        promo.getQuantity.trim().length > 0 &&
        promo.getDiscountPercent.trim().length > 0 &&
        !Number.isNaN(buy) &&
        !Number.isNaN(get) &&
        !Number.isNaN(percent);
      if (!ready) return t(keys.coupons.previewBogoUnset);
      const clampedPercent = Math.min(Math.max(percent, 0), 100);
      return clampedPercent >= 100
        ? t(keys.coupons.previewBogoFree, { code: codeLabel, buy, get })
        : t(keys.coupons.previewBogoPercent, {
            code: codeLabel,
            buy,
            get,
            percent: clampedPercent,
          });
    }
    // CART_DISCOUNT + PRODUCT_DISCOUNT
    const isProduct = kind === "PRODUCT_DISCOUNT";
    const numericValue = Number(discountValue);
    const hasValue =
      discountValue.trim().length > 0 &&
      !Number.isNaN(numericValue) &&
      numericValue > 0;
    if (!hasValue) {
      return isProduct
        ? t(keys.coupons.previewProductDiscountUnset, { code: codeLabel })
        : t(keys.coupons.previewCartDiscountUnset, { code: codeLabel });
    }
    if (discountUnit === "PERCENT") {
      const clamped = Math.min(numericValue, 100);
      return isProduct
        ? t(keys.coupons.previewProductDiscountPercent, {
            code: codeLabel,
            percent: clamped,
          })
        : t(keys.coupons.previewCartDiscountPercent, {
            code: codeLabel,
            percent: clamped,
          });
    }
    const amount = formatCurrency(numericValue, locale);
    return isProduct
      ? t(keys.coupons.previewProductDiscountFixed, { code: codeLabel, amount })
      : t(keys.coupons.previewCartDiscountFixed, { code: codeLabel, amount });
  }, [normalizedCode, promo, locale, t]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitError(null);
    const errors = validate();
    const pErrors = validatePromotionState(promo, t, couponPromotionKeys);
    if (errors || pErrors) {
      setFieldErrors(errors ?? {});
      setPromoErrors(pErrors ?? {});
      setCodeTouched(true);
      return;
    }
    setFieldErrors({});
    setPromoErrors({});

    const parsedMaxUses = maxUses.trim().length > 0 ? Number(maxUses) : null;
    const parsedMinOrder =
      minOrderAmount.trim().length > 0 ? Number(minOrderAmount) : null;
    const parsedExpiresAt =
      expiresAt.trim().length > 0 ? new Date(expiresAt).toISOString() : null;
    const promotionFields = buildPromotionPayloadFields(promo);

    try {
      if (mode === "create") {
        await createCoupon({
          storeSlug,
          code: normalizedCode,
          ...promotionFields,
          maxUses: parsedMaxUses,
          active,
          minOrderAmount: parsedMinOrder,
          expiresAt: parsedExpiresAt,
        });
      } else if (coupon) {
        await updateCoupon({
          storeSlug,
          couponId: coupon.id,
          code: normalizedCode,
          ...promotionFields,
          maxUses: parsedMaxUses,
          active,
          minOrderAmount: parsedMinOrder,
          expiresAt: parsedExpiresAt,
        });
      }
      onSuccess();
    } catch (error) {
      setSubmitError(
        error instanceof Error ? error.message : t(keys.errors.generic),
      );
    }
  };

  const handleCodeBlur = () => {
    setCodeTouched(true);
    if (code.length > 0) {
      const codeError = validateCode();
      setFieldErrors((prev) => ({ ...prev, code: codeError }));
    }
  };

  const handleCodeChange = (value: string) => {
    setCode(value.toUpperCase());
    if (fieldErrors.code) {
      setFieldErrors((prev) => ({ ...prev, code: undefined }));
    }
  };

  const showCodeError = codeTouched && fieldErrors.code;
  const showCodeSuccess = codeTouched && !fieldErrors.code && codeIsValid;

  // Surface the submit state to the parent so an external footer Save button
  // (rendered in the Modal footer) can disable while a mutation is in flight.
  // Using useMemo + ref pattern would be cleaner but a synchronous useEffect
  // is fine here since this prop is optional.
  useEffect(() => {
    onSubmittingChange?.(isSubmitting);
  }, [isSubmitting, onSubmittingChange]);

  return (
    <form id={formId} onSubmit={handleSubmit} className="flex flex-col gap-5">
      {submitError ? (
        <Alert message={submitError} helperText={t(keys.errors.recovery)} />
      ) : null}

      {/* Live preview banner */}
      <div
        className="flex items-start gap-3 rounded-xl border border-[color:var(--accent-500)]/20 bg-gradient-to-br from-[color:var(--accent-500)]/5 to-transparent px-4 py-3"
        aria-live="polite"
      >
        <div
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[color:var(--accent-500)]/15 text-[color:var(--accent-600)]"
          aria-hidden="true"
        >
          {promo.kind === "FREE_DELIVERY" ? <TruckIcon /> : <PercentIcon />}
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="text-[10.5px] font-semibold uppercase tracking-[0.06em] text-[color:var(--text-muted)]">
            {t(keys.coupons.previewLabel)}
          </span>
          <span className="text-[13.5px] leading-snug text-[color:var(--text-primary)]">
            {previewText}
          </span>
        </div>
      </div>

      {/* Code */}
      <FormField
        label={t(keys.coupons.codeLabel)}
        name="code"
        required
        error={showCodeError ? fieldErrors.code : undefined}
        helperText={
          showCodeError
            ? undefined
            : `${normalizedCode.length}/${CODE_MAX_LENGTH} · ${t(keys.coupons.codeHint)}`
        }
      >
        <div className="relative">
          <TextInput
            name="code"
            value={code}
            placeholder={t(keys.coupons.codePlaceholder)}
            required
            autoCapitalize="characters"
            autoComplete="off"
            maxLength={CODE_MAX_LENGTH}
            hasError={Boolean(showCodeError)}
            onBlur={handleCodeBlur}
            onChange={(event) => handleCodeChange(event.target.value)}
            className="font-mono uppercase tracking-[0.05em]"
          />
          {showCodeSuccess ? (
            <span
              className="pointer-events-none absolute inset-y-0 end-2.5 flex items-center text-[color:var(--success-500)]"
              aria-hidden="true"
            >
              <CheckIcon />
            </span>
          ) : null}
        </div>
      </FormField>

      {/* Kind + value / BOGO / scope */}
      <PromotionFields
        k={couponPromotionKeys}
        storeSlug={storeSlug}
        locale={locale}
        value={promo}
        onChange={updatePromo}
        errors={promoErrors}
        onClearError={clearPromoError}
      />

      {/* Limits */}
      <FormField
        label={t(keys.coupons.maxUsesLabel)}
        name="maxUses"
        error={fieldErrors.maxUses}
        helperText={t(keys.coupons.maxUsesHint)}
      >
        <TextInput
          name="maxUses"
          type="number"
          inputMode="numeric"
          step="1"
          min="1"
          value={maxUses}
          placeholder={t(keys.coupons.maxUsesPlaceholder)}
          hasError={Boolean(fieldErrors.maxUses)}
          onChange={(event) => {
            setMaxUses(event.target.value);
            if (fieldErrors.maxUses) {
              setFieldErrors((prev) => ({ ...prev, maxUses: undefined }));
            }
          }}
        />
      </FormField>

      {/* Minimum order + expiry */}
      <div className="grid gap-4 sm:grid-cols-2">
        <FormField
          label={t(keys.coupons.minOrderLabel)}
          name="minOrderAmount"
          error={fieldErrors.minOrderAmount}
          helperText={
            fieldErrors.minOrderAmount
              ? undefined
              : t(keys.coupons.minOrderHint)
          }
        >
          <div className="relative">
            <TextInput
              name="minOrderAmount"
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0.01"
              value={minOrderAmount}
              placeholder={t(keys.coupons.minOrderPlaceholder)}
              hasError={Boolean(fieldErrors.minOrderAmount)}
              onChange={(event) => {
                setMinOrderAmount(event.target.value);
                if (fieldErrors.minOrderAmount) {
                  setFieldErrors((prev) => ({
                    ...prev,
                    minOrderAmount: undefined,
                  }));
                }
              }}
              className="pe-10"
            />
            <span
              className="pointer-events-none absolute inset-y-0 end-3 flex items-center text-[12px] font-semibold text-[color:var(--text-muted)]"
              aria-hidden="true"
            >
              EGP
            </span>
          </div>
        </FormField>
        <FormField
          label={t(keys.coupons.expiryLabel)}
          name="expiresAt"
          helperText={t(keys.coupons.expiryHint)}
        >
          <TextInput
            name="expiresAt"
            type="datetime-local"
            value={expiresAt}
            onChange={(event) => setExpiresAt(event.target.value)}
          />
        </FormField>
      </div>

      {/* Active toggle */}
      <div className="rounded-xl border border-[color:var(--border-light)] bg-[color:var(--surface-alt)] px-4 py-3">
        <div className="flex items-center justify-between gap-3">
          <span className="text-[13px] font-semibold text-[color:var(--text-primary)]">
            {t(keys.coupons.activeLabel)}
          </span>
          <ToggleInput
            name="active"
            checked={active}
            onChange={(event) => setActive(event.target.checked)}
            ariaLabel={t(keys.coupons.activeLabel)}
          />
        </div>
        <p className="mt-1 text-[12px] leading-snug text-[color:var(--text-muted)]">
          {t(keys.coupons.activeHint)}
        </p>
      </div>

      {/* Usage stats (edit mode only) */}
      {mode === "edit" && coupon ? (
        <div className="flex items-center justify-between rounded-xl border border-[color:var(--border-light)] bg-[color:var(--surface)] px-4 py-3">
          <span className="text-[12px] font-semibold uppercase tracking-[0.05em] text-[color:var(--text-muted)]">
            {t(keys.coupons.usageStatLabel)}
          </span>
          <span className="font-mono text-[13px] font-semibold text-[color:var(--text-primary)]">
            {coupon.usesCount} /{" "}
            {coupon.maxUses !== null
              ? coupon.maxUses
              : t(keys.coupons.maxUsesUnlimited)}
          </span>
        </div>
      ) : null}

      {/* Inline action buttons are only rendered when no external formId is
          supplied. When a Modal wraps the form, the Modal footer hosts the
          Cancel/Save buttons and submits via `form="<id>"`. */}
      {formId ? null : (
        <div className="mt-1">
          <FormActionButtons
            cancelLabel={t(keys.common.cancel)}
            saveLabel={
              mode === "create"
                ? t(keys.coupons.createAction)
                : t(keys.common.save)
            }
            loadingLabel={t(keys.common.loading)}
            onCancel={onCancel}
            isSaving={isSubmitting}
          />
        </div>
      )}
    </form>
  );
}

function TruckIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="1" y="3" width="15" height="13" />
      <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
      <circle cx="5.5" cy="18.5" r="2.5" />
      <circle cx="18.5" cy="18.5" r="2.5" />
    </svg>
  );
}

function PercentIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="19" y1="5" x2="5" y2="19" />
      <circle cx="6.5" cy="6.5" r="2.5" />
      <circle cx="17.5" cy="17.5" r="2.5" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}
